import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { apiPost } from '@/api/client';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useSubmit } from '@/lib/useSubmit';

interface ErasureReport {
  erasure_id?: string;
  departments_without_admin?: string[];
  ingest_measurements_deleted?: boolean;
}

/**
 * Admin only and irreversible: hard-deletes the identity's user row,
 * memberships, consents and grants. The explicit typed confirmation is the
 * point of this page — erasure must never be one accidental click.
 */
export function ErasePage() {
  const { t } = useTranslation();
  const { userId = '' } = useParams();
  const [confirmation, setConfirmation] = useState('');
  const [report, setReport] = useState<ErasureReport | null>(null);
  const form = useSubmit(async () => {
    const result = (await apiPost('/users/{userId}/erase', { params: { userId } })) as
      ErasureReport | undefined;
    setReport(result ?? {});
  });
  const confirmWord = t('manage.erase.confirmWord');

  if (report) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('manage.erase.completedTitle')} />
        <div role="status" className="flex flex-col gap-3">
          <p className="text-ink text-sm">{t('manage.erase.completedText')}</p>
          {(report.departments_without_admin?.length ?? 0) > 0 && (
            <Alert tone="warning">
              {t('manage.erase.departmentsWithoutAdmin', {
                names: report.departments_without_admin?.join(', ') ?? '',
              })}
            </Alert>
          )}
          {report.ingest_measurements_deleted === false && (
            <Alert tone="warning">{t('manage.erase.measurementsNotDeleted')}</Alert>
          )}
          <Link className="text-accent text-sm" to="/manage/users">
            {t('manage.erase.backToUsers')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('manage.erase.title')} />
      <form
        className="flex max-w-2xl flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <p className="text-ink text-sm">{t('manage.erase.lead', { userId, confirmWord })}</p>
        <Field label={t('manage.erase.confirmationLabel')}>
          <Input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={confirmWord}
          />
        </Field>
        <SubmitError error={form.error} />
        <FormActions>
          <Button as={Link} to="/manage/users" variant="ghost">
            {t('manage.erase.cancel')}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={form.pending || confirmation !== confirmWord}
          >
            {t('manage.erase.erasePermanently')}
          </Button>
        </FormActions>
      </form>
    </section>
  );
}
